import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Autenticator.css";

import LoginForm from "./LoginForm/LoginForm";
import ForgotPassword from "./ForgotPassword/ForgotPassword";
import Register from "./Register/Register";
import ResetPassword from "./ResetPassword/ResetPassword";
import owlyLogo from "../OwlyManage/assets/owly-logo.png";
import { clearAllAuthData } from "../context/cookieUtils";
import { useTranslation } from "react-i18next";
import LanguageFlagSwitcher from "./LanguageFlagSwitcher";
import useUser from "../hooks/useUser";
import { hasAdminAccess } from "../utils/authz";

export default function Authenticator({ setUser, onAuthenticated }) {
  const navigate = useNavigate();
  const params = useParams();
  const { user } = useUser();
  const { t } = useTranslation('auth');


  // Redirige a /app si el usuario está logueado y tiene acceso admin
  useEffect(() => {
    if (user && hasAdminAccess(user)) {
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);

  // Extraer uid y token de los parámetros de la URL
  const { uid, token } = params;

  // Render según la ruta
  const path = window.location.pathname;

  // Si la ruta incluye /reset_password/ con parámetros
  if (path.includes("/reset_password/") && uid && token) {
    return (
      <div className="chatbot-auth-bg">
        <div className="chatbot-auth-box">
          <LanguageFlagSwitcher />
          <img src={owlyLogo} alt="Owly Logo" />
          <ResetPassword
            uid={uid}
            token={token}
            onCancel={() => navigate("/login")}
          />
        </div>
      </div>
    );
  }

  if (path === "/register") {
    return (
      <div className="chatbot-auth-bg">
        <div className="chatbot-auth-box">
          <LanguageFlagSwitcher />
          <img src={owlyLogo} alt="Owly Logo" />
          <Register onBack={() => navigate("/login")} />
        </div>
      </div>
    );
  }

  if (path === "/forgot_password") {
    return (
      <div className="chatbot-auth-bg">
        <div className="chatbot-auth-box">
          <LanguageFlagSwitcher />
          <img src={owlyLogo} alt="Owly Logo" />
          <ForgotPassword onCancel={() => navigate("/login")} />
        </div>
      </div>
    );
  }

  if (path === "/login" || path === "/") {
    return (
      <div className="chatbot-auth-bg">
        <div className="chatbot-auth-box">
          <LanguageFlagSwitcher />
          <img src={owlyLogo} alt="Owly Logo" />
          <LoginForm
            setUser={setUser}
            onAuthenticated={onAuthenticated}
            onRegisterLink={() => navigate("/register")}
          />
          <div className="auth-buttons">
            <button
              type="button"
              className="forgot-password-link"
              onClick={() => navigate("/forgot_password")}
              style={{ marginTop: 8 }}
            >
              {t('login.forgot')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  // fallback
  return null;
}

export async function logoutUser({ setUser, token } = {}) {
  try {
    const logoutUrl = process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_BASE_URL_PROD + "/api/auth/logout/"
      : process.env.REACT_APP_BASE_URL_DEV + "/api/auth/logout/";
    const res = await fetch(logoutUrl, {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    // 401/403 son aceptables si el token ya expiró o no hay sesión
    if (!res.ok && res.status !== 401 && res.status !== 403) {
      console.warn("Logout API returned status:", res.status);
    }
  } catch (error) {
    // No bloquear logout local por errores de red/CORS
    console.warn("Error during logout API call:", error);
  }

  // Limpiar completamente todos los datos de autenticación
  clearAllAuthData();

  // Teardown inmediato del chat embebido
  try { window.owlyTeardownChatNow?.(); } catch (_) {}

  // Reintentos cortos para capturar re-render tardío del embed (durante navegación)
  try {
    const start = Date.now();
    const interval = setInterval(() => {
      try { window.owlyTeardownChatNow?.(); } catch (_) {}
      if (Date.now() - start > 2500) clearInterval(interval);
    }, 200);
  } catch (_) {}

  // Notificar logout a otras partes de la app (SSO simple)
  try {
    // Hijos (iframes)
    if (window.frames && window.frames.length > 0) {
      for (let i = 0; i < window.frames.length; i++) {
        window.frames[i].postMessage({ type: "SSO_LOGOUT" }, "*");
      }
    }
    // Padre (si estamos embebidos)
    if (window.parent !== window) {
      window.parent.postMessage({ type: "SSO_LOGOUT" }, "*");
    }
    // Otras pestañas del mismo origen
    localStorage.setItem("logout-event", Date.now().toString());
    localStorage.removeItem("logout-event");
  } catch (error) {
    console.warn("Error notifying logout to connected applications:", error);
  }

  if (setUser) setUser(null);

  // Forzar recarga dura para asegurar que el embed no persista en memoria
  // Pequeño delay para permitir que el teardown/envíos postMessage se apliquen
  setTimeout(() => {
    try {
      // Volver a la pantalla raíz de autenticación con recarga completa
      window.location.replace("/");
    } catch (_) {
      window.location.href = "/";
    }
  }, 150);
}
